'use client'

import { useCallback, useEffect, useRef } from 'react'

import type { LogEntry, LogLevel } from '../types'
import { createLogHash, getStackTrace, serializeArgs } from '../utils'

const EXCLUDED_LOG_PATTERNS = [
	'[Fast Refresh]',
	'[HMR]',
	'[webpack',
	'hot-update',
	'Download the React DevTools',
	'%c[RenderDebug]'
]

const shouldExcludeLog = (message: string): boolean => {
	return EXCLUDED_LOG_PATTERNS.some(pattern => message.includes(pattern))
}

interface UseConsoleInterceptorOptions {
	onLog: (entry: Omit<LogEntry, 'id' | 'timestamp'>) => void
	maxDuplicateWindow?: number
}

interface OriginalConsoleMethods {
	log: typeof console.log
	warn: typeof console.warn
	error: typeof console.error
}

export const useConsoleInterceptor = ({ onLog, maxDuplicateWindow = 1000 }: UseConsoleInterceptorOptions): void => {
	const originalMethodsRef = useRef<OriginalConsoleMethods | null>(null)
	const recentLogsRef = useRef<Map<string, number>>(new Map())

	const isDuplicate = useCallback(
		(level: LogLevel, message: string): boolean => {
			const hash = createLogHash(level, message)
			const now = Date.now()
			const lastTime = recentLogsRef.current.get(hash)

			if (lastTime && now - lastTime < maxDuplicateWindow) {
				return true
			}

			recentLogsRef.current.set(hash, now)

			if (recentLogsRef.current.size > 100) {
				const entries = Array.from(recentLogsRef.current.entries())
				const cutoff = now - maxDuplicateWindow
				recentLogsRef.current = new Map(entries.filter(([, time]) => time > cutoff))
			}

			return false
		},
		[maxDuplicateWindow]
	)

	const createInterceptor = useCallback(
		(level: LogLevel, originalMethod: (...args: unknown[]) => void) => {
			return (...args: unknown[]): void => {
				originalMethod.apply(console, args)

				const message = serializeArgs(args)
				if (shouldExcludeLog(message)) return
				if (isDuplicate(level, message)) return

				const source = getStackTrace()

				// Defer to avoid setState during render when console methods are called during component render
				queueMicrotask(() => {
					onLog({
						level,
						message,
						args,
						source
					})
				})
			}
		},
		[onLog, isDuplicate]
	)

	useEffect(() => {
		originalMethodsRef.current = {
			log: console.log,
			warn: console.warn,
			error: console.error
		}

		console.log = createInterceptor('log', originalMethodsRef.current.log)
		console.warn = createInterceptor('warn', originalMethodsRef.current.warn)
		console.error = createInterceptor('error', originalMethodsRef.current.error)

		return () => {
			if (originalMethodsRef.current) {
				console.log = originalMethodsRef.current.log
				console.warn = originalMethodsRef.current.warn
				console.error = originalMethodsRef.current.error
			}
		}
	}, [createInterceptor])
}
